import React, { useState, forwardRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import Slider from "react-slick";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GlassPanel, ModernTextField } from "./styledComponents";

import PectoralMasculino from "../../assets/images/plieguescutaneos/pectoral.png";
import PectoralFemenino from "../../assets/images/plieguescutaneos/pectoralMujer.png";
import TricipitalMasculino from "../../assets/images/plieguescutaneos/tricipital.png";
import TricipitalFemenino from "../../assets/images/plieguescutaneos/tricipitalMujer.png";
import BicipitalMasculino from "../../assets/images/plieguescutaneos/bicipital.png";
import BicipitalFemenino from "../../assets/images/plieguescutaneos/bicipitalMujer.png";
import SubescapularMasculino from "../../assets/images/plieguescutaneos/subescapular.png";
import SubescapularFemenino from "../../assets/images/plieguescutaneos/subescapularMujer.png";
import MidaxilarMasculino from "../../assets/images/plieguescutaneos/midaxilar.png";
import MidaxilarFemenino from "../../assets/images/plieguescutaneos/midaxilarMujer.png";
import SuprailiacoMasculino from "../../assets/images/plieguescutaneos/suprailiaco.png";
import SuprailiacoFemenino from "../../assets/images/plieguescutaneos/suprailiacoMujer.png";
import AbdominalMasculino from "../../assets/images/plieguescutaneos/abdominal.png";
import AbdominalFemenino from "../../assets/images/plieguescutaneos/abdominalMujer.png";
import MusloMedioMasculino from "../../assets/images/plieguescutaneos/musloMedio.png";
import MusloMedioFemenino from "../../assets/images/plieguescutaneos/musloMedioMujer.png";
import PantorrillaMediaMasculino from "../../assets/images/plieguescutaneos/pantorrillaMedia.png";
import PantorrillaMediaFemenino from "../../assets/images/plieguescutaneos/pantorrillaMediaMujer.png";

const CarouselArrow = ({ className, style, onClick, direction }) => (
  <IconButton
    className={className}
    onClick={onClick}
    style={style}
    sx={{
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundColor: "#fff",
      boxShadow: "0 10px 25px rgba(15,23,42,0.15)",
      "&:hover": { backgroundColor: "#f1f5f9" },
      ...(direction === "next" ? { right: -24 } : { left: -24 }),
    }}
  >
    {direction === "next" ? (
      <ArrowForwardIosIcon fontSize="small" />
    ) : (
      <ArrowBackIosNewIcon fontSize="small" />
    )}
  </IconButton>
);

const PlieguesCutaneos = forwardRef(
  ({ formValues, formValuesBasic, onChange }, ref) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);

    const handleInputChange = useCallback(
      (e) => {
        const { name, value } = e.target;
        onChange(name, value);
      },
      [onChange]
    );

    const handleImageClick = useCallback((image) => {
      setCurrentImage(image);
      setOpenDialog(true);
    }, []);

    const handleDialogClose = useCallback(() => {
      setOpenDialog(false);
    }, []);

    const today = useMemo(() => new Date().toISOString().split("T")[0], []);

    const carouselImages = useMemo(() => {
      const isFemale = formValuesBasic?.sexo === "femenino";
      return [
        {
          label: "Pectoral",
          src: isFemale ? PectoralFemenino : PectoralMasculino,
        },
        {
          label: "Tricipital",
          src: isFemale ? TricipitalFemenino : TricipitalMasculino,
        },
        {
          label: "Bicipital",
          src: isFemale ? BicipitalFemenino : BicipitalMasculino,
        },
        {
          label: "Subescapular",
          src: isFemale ? SubescapularFemenino : SubescapularMasculino,
        },
        {
          label: "Midaxilar",
          src: isFemale ? MidaxilarFemenino : MidaxilarMasculino,
        },
        {
          label: "Suprailiaco",
          src: isFemale ? SuprailiacoFemenino : SuprailiacoMasculino,
        },
        {
          label: "Abdominal",
          src: isFemale ? AbdominalFemenino : AbdominalMasculino,
        },
        {
          label: "Muslo Medio",
          src: isFemale ? MusloMedioFemenino : MusloMedioMasculino,
        },
        {
          label: "Pantorrilla Media",
          src: isFemale ? PantorrillaMediaFemenino : PantorrillaMediaMasculino,
        },
      ];
    }, [formValuesBasic?.sexo]);

    const carouselSettings = useMemo(
      () => ({
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 6,
        slidesToScroll: 1,
        nextArrow: <CarouselArrow direction="next" />,
        prevArrow: <CarouselArrow direction="prev" />,
        responsive: [
          { breakpoint: 1280, settings: { slidesToShow: 4 } },
          { breakpoint: 900, settings: { slidesToShow: 3 } },
          { breakpoint: 600, settings: { slidesToShow: 2 } },
        ],
      }),
      []
    );

    const inputFields = useMemo(
      () => [
        { label: "Pectoral (mm)", name: "pectoral" },
        { label: "Tricipital (mm)", name: "tricipital" },
        { label: "Bicipital (mm)", name: "bicipital" },
        { label: "Subescapular (mm)", name: "subescapular" },
        { label: "Midaxilar (mm)", name: "midaxilar" },
        { label: "Suprailiaco (mm)", name: "suprailiaco" },
        { label: "Abdominal (mm)", name: "abdominal" },
        { label: "Muslo Medio (mm)", name: "musloMedio" },
        { label: "Pantorrilla media (mm)", name: "pantorrillaMedia" },
      ],
      []
    );

    return (
      <Box ref={ref} sx={{ py: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <GlassPanel>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 3 }}>
                  Pliegues cutáneos
                </Typography>
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  Controla tu composición
                </Typography>
                <Typography color="text.secondary">
                  Usa un plicómetro o tus registros previos para ingresar cada
                  medición. Las imágenes interactivos te guían en la ubicación
                  correcta.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {["Plicómetro", "Referencias", "Seguimiento"].map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
              <Box sx={{ position: "relative", px: { xs: 3, sm: 4 } }}>
                <Slider {...carouselSettings}>
                  {carouselImages.map((image) => (
                    <Box key={image.label} sx={{ px: 1, textAlign: "center" }}>
                      <Box
                        onClick={() => handleImageClick(image.src)}
                        sx={{
                          position: "relative",
                          mx: "auto",
                          width: 110,
                          height: 110,
                          borderRadius: 4,
                          overflow: "hidden",
                          cursor: "pointer",
                          boxShadow: "0 15px 30px rgba(15,23,42,0.18)",
                        }}
                      >
                        <img
                          src={image.src}
                          alt={image.label}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          draggable="false"
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(180deg, transparent, rgba(15,23,42,0.55))",
                            color: "#fff",
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontWeight: 600 }}
                      >
                        {image.label}
                      </Typography>
                    </Box>
                  ))}
                </Slider>
              </Box>
            </Stack>
          </GlassPanel>

          <GlassPanel>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ModernTextField
                  label="Fecha"
                  name="fechaPliegues"
                  value={formValues.fechaPliegues || ""}
                  onChange={handleInputChange}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today }}
                  helperText="Ingresa la fecha en la que fueron tomadas estas medidas"
                  sx={{
                    "& input[type='date']::-webkit-calendar-picker-indicator": {
                      cursor: "pointer",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Si no cuentas con los datos, puedes dejar el campo vacío y
                  capturarlo más adelante. Registra cada medición en milímetros.
                </Typography>
              </Grid>
              {inputFields.map((field) => (
                <Grid item xs={12} md={6} key={field.name}>
                  <ModernTextField
                    label={field.label}
                    name={field.name}
                    value={formValues[field.name] || ""}
                    onChange={handleInputChange}
                    type="number"
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>
          </GlassPanel>
        </Stack>

        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md">
          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 0,
            }}
          >
            <img
              src={currentImage}
              alt="Referencia de pliegues"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }
);

export default PlieguesCutaneos;
